import type { MouseEvent } from "react";
import { Eye } from "lucide-react";
import { Tooltip } from "../molecules/Tooltip/Tooltip";
import type {
  PlayerAIProposalCanvasDestination,
  PlayerAIProposalCanvasRevealOutcome,
} from "./playerai.types";

/**
 * "View on canvas" — the one action that connects a saved proposal to what it
 * actually did.
 *
 * Icon-only, and separate from the disclosure it sits on. Three rules are
 * load-bearing, and each is an acceptance criterion rather than a preference:
 *
 * - It NEVER toggles the row it sits on. It is a SIBLING of the `<details>`,
 *   positioned over the summary — nesting it inside the `<summary>` would make
 *   the disclosure and the navigation one tab stop with one ambiguous name
 *   (axe `nested-interactive`). The default is cancelled and the event stopped
 *   here as well, so the same button is safe on a row that is not a disclosure.
 *   Somebody who wants the detail presses the row; somebody who wants the
 *   widget presses this.
 * - It is a real `<button>` with its own focus ring and a hit area big enough
 *   to press. The tooltip shows on hover AND keyboard focus, and the accessible
 *   name says which widget it leads to — several of these rows sit one under
 *   another, and "View on canvas" five times is a list of identical buttons to
 *   a screen reader.
 * - A refusal is SAID. The host answers synchronously with what happened, so a
 *   deleted or unreachable destination becomes a sentence under the row rather
 *   than a button that appears to do nothing. The outcome is held by the ROW,
 *   which owns the layout the sentence belongs in.
 */

export function ProposalCanvasDestinationButton({
  destination,
  onViewOnCanvas,
  onOutcome,
}: {
  readonly destination: PlayerAIProposalCanvasDestination;
  readonly onViewOnCanvas: (
    widgetId: string,
  ) => PlayerAIProposalCanvasRevealOutcome;
  readonly onOutcome: (outcome: PlayerAIProposalCanvasRevealOutcome) => void;
}) {
  const label = destination.phase
    ? `View ${destination.label} on canvas, in ${destination.phase}`
    : `View ${destination.label} on canvas`;
  return (
    <Tooltip content="View on canvas">
      <button
        type="button"
        aria-label={label}
        onClick={(event: MouseEvent<HTMLButtonElement>) => {
          // A `<summary>`'s default action is to toggle its `<details>`.
          // Cancelling it here is what keeps navigation and disclosure two
          // separate gestures on the same row.
          event.preventDefault();
          event.stopPropagation();
          onOutcome(onViewOnCanvas(destination.widgetId));
        }}
        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-transparent text-pb-text-muted transition-colors hover:pb-overlay-paint hover:text-pb-text focus-ring"
      >
        <Eye aria-hidden className="h-3.5 w-3.5" />
      </button>
    </Tooltip>
  );
}

/**
 * What a refused navigation says. Visible AND announced: `role="status"` on the
 * paragraph the reader can see, rather than a separate hidden copy that could
 * drift from it.
 */
export function ProposalCanvasDestinationNotice({
  outcome,
}: {
  readonly outcome: PlayerAIProposalCanvasRevealOutcome | null;
}) {
  if (!outcome || outcome.status === "revealed") return null;
  return (
    <p role="status" className="mt-1 text-dense-1 text-pb-text-muted">
      {revealMessage(outcome)}
    </p>
  );
}

export function revealMessage(
  outcome: PlayerAIProposalCanvasRevealOutcome,
): string {
  switch (outcome.status) {
    case "missing":
      return "That widget is no longer on this canvas.";
    case "unavailable":
      return "The canvas isn't available right now. Try again in a moment.";
    case "hidden":
      return (
        outcome.reason ??
        "That widget isn't visible on this canvas for you right now."
      );
    default:
      return "";
  }
}
